import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { ArrowUp, RefreshCw } from "lucide-react";

import { Header } from "../../../components/Header/Header";
import useNotificationsStore from "../../../store/useNotificationsStore";
import {
  dismissMatch,
  fetchDiscover,
  fetchMatchProfile,
  markDiscoverViewed,
  refreshDiscover,
  saveMatch,
  updateMatchProfile,
} from "../../../services/tendersApi";
import { showErrorToast, showSuccessToast } from "../../../utils/toast";
import { switchWorkspace } from "../../../utils/workspace";
import CopilotTenderPanel from "../Copilot/CopilotTenderPanel";
import DiscoverCard from "./DiscoverCard";
import DiscoverRail from "./DiscoverRail";
import MatchSetupModal from "./MatchSetupModal";
import TenderModeSwitch from "./TenderModeSwitch";
import { CONTENT_CLASS, Callout, RAIL_CLASS, RAIL_CLASS_WITH_PANEL, SortSelect, TOP_BAR_CLASS } from "./TenderUI";

/* Discover — tenders that fit the workspace, found without a search.
 *
 * The matching runs in the background after every tender sync (and when Memory
 * or the match profile changes); this page reads what it kept. While a run is
 * going the page says so and polls, so a first visit fills itself in.
 */

const PAGE_SIZE = 20;
const POLL_MS = 5000;
const SETUP_SEEN = "discover:setupOffered";

const TABS = [
  { key: "for_you", label: "For you", stat: "total" },
  { key: "new", label: "New", stat: "new" },
  { key: "saved", label: "Saved", stat: "saved" },
  { key: "not_a_fit", label: "Not a fit", stat: "not_a_fit" },
];

const SORTS = [
  { value: "best", label: "Best fit" },
  { value: "newest", label: "Newest first" },
  { value: "closing", label: "Closing soonest" },
];

const ago = (iso) => {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Freshness, as one compact chip beside the mode switch: the state and one fact
// in the bar, the whole account on hover. Refresh sits next to it at the
// height of every other top-bar control.
function StatusChip({ status, onRefresh, refreshing }) {
  if (!status) return null;
  const tone = {
    live: { dot: "bg-[#16A34A]", label: "Live" },
    updating: { dot: "bg-[#A04A97] animate-pulse", label: "Updating" },
    matching: { dot: "bg-[#A04A97] animate-pulse", label: "Matching" },
    delayed: { dot: "bg-[#D97706]", label: "Delayed" },
  }[status.state] || { dot: "bg-[#9CA3AF]", label: "" };
  const short = {
    live: status.matched_at ? `Matched ${ago(status.matched_at)}` : `Updated ${ago(status.synced_at)}`,
    updating: "Collecting today's tenders",
    matching: "Checking new tenders",
    delayed: status.synced_at ? `Feed last updated ${ago(status.synced_at)}` : "Feed hasn't run yet",
  }[status.state];
  const detail = [
    status.synced_at && `Updated from SAM.gov ${ago(status.synced_at)}`,
    status.scanned && `${status.scanned.toLocaleString()} new and updated tenders checked`,
    status.matched_at && `Matched ${ago(status.matched_at)}`,
    status.state === "updating" && "Your matches stay usable meanwhile",
    status.state === "delayed" && "Showing your last matches",
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="flex min-w-0 items-center gap-1.5 font-outfit">
      <span
        title={detail}
        className="inline-flex h-[35px] min-w-0 items-center gap-2 rounded-md border border-[#00000014] bg-[#FAFAFA] px-3 text-[13px]"
      >
        <span className={`h-2 w-2 flex-none rounded-full ${tone.dot}`} />
        <span className="flex-none font-semibold text-[#18101C]">{tone.label}</span>
        <span className="truncate text-[#77707E]">{short}</span>
      </span>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        aria-label="Match again now"
        title="Match again now"
        className="grid h-[35px] w-[35px] flex-none place-items-center rounded-md border border-[#00000024] bg-white text-[#8E4187] transition-colors hover:bg-[#FBF2FA] disabled:opacity-50"
      >
        <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} strokeWidth={2.2} />
      </button>
    </div>
  );
}

function StatTile({ value, label, tone = "text-[#18101C]" }) {
  return (
    <div className="rounded-2xl border border-[#18101C]/[0.08] bg-white px-4 py-3.5">
      <p className={`text-[26px] font-semibold leading-none ${tone}`}>{value ?? "–"}</p>
      <p className="mt-1.5 text-[13px] text-[#52525B]">{label}</p>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="mb-3 flex gap-5 rounded-2xl border border-[#E9DCE7] bg-white px-5 py-4">
      <div className="h-16 w-16 flex-none animate-pulse rounded-full bg-[#F4ECF3]" />
      <div className="flex-1 space-y-2 pt-1">
        <div className="h-4 w-1/3 animate-pulse rounded bg-[#F4F4F5]" />
        <div className="h-4 w-3/4 animate-pulse rounded bg-[#F4F4F5]" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-[#F4F4F5]" />
      </div>
    </div>
  );
}

function EmptyState({ title, body, action, onAction, progress }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#D9C3D6] bg-white px-6 py-12 text-center font-outfit">
      <h3 className="text-[17px] font-semibold text-[#18101C]">{title}</h3>
      <p className="mx-auto mt-1.5 max-w-[460px] text-[14px] leading-relaxed text-[#6B6472]">{body}</p>
      {progress && (
        <div className="mx-auto mt-5 h-1.5 max-w-[320px] overflow-hidden rounded-full bg-[#F1E6EF]">
          <div className="h-full w-2/3 animate-pulse rounded-full bg-[#A04A97]" />
        </div>
      )}
      {action && (
        <button
          type="button"
          onClick={onAction}
          className="mt-4 text-[14px] font-semibold text-[#8E4187] underline-offset-2 hover:underline"
        >
          {action}
        </button>
      )}
    </div>
  );
}

// Email and bell links arrive as ?org=<workspace>: switch to it first, the way a
// saved-search link does, or the feed shown would be another workspace's.
const openWorkspace = (orgId) => {
  if (!orgId || orgId === Cookies.get("selected_org_id")) return;
  let workspaces = [];
  try {
    workspaces = JSON.parse(localStorage.getItem("cachedWorkspaces") || "[]");
  } catch {
    /* no cached list: stay where we are */
  }
  const workspace = Array.isArray(workspaces) && workspaces.find((w) => String(w.id) === String(orgId));
  if (workspace) switchWorkspace(workspace);
};

export default function TenderDiscover() {
  const navigate = useNavigate();
  const { isMenuOpen } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState("for_you");
  const [sort, setSort] = useState("best");
  const [data, setData] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [setup, setSetup] = useState(null); // {step} while the profile modal is open
  const [panelId, setPanelId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pill, setPill] = useState(0);
  const requestRef = useRef(0);

  // Before the first fetch, so it reads the right workspace.
  const [orgReady, setOrgReady] = useState(false);
  useEffect(() => {
    const org = searchParams.get("org");
    if (org) {
      openWorkspace(org);
      const next = new URLSearchParams(searchParams);
      next.delete("org");
      setSearchParams(next, { replace: true });
    }
    setOrgReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(
    async (offset = 0, { silent = false } = {}) => {
      const ticket = ++requestRef.current;
      if (offset) setLoadingMore(true);
      else if (!silent) setLoading(true);
      setError(null);
      try {
        const body = await fetchDiscover({ tab, sort, limit: PAGE_SIZE, offset });
        if (ticket !== requestRef.current) return;
        setData(body);
        setResults((prev) => (offset ? [...prev, ...body.results] : body.results));
      } catch (err) {
        if (ticket !== requestRef.current) return;
        setError(
          err?.response?.status === 403
            ? "Tender Discover is not available for this workspace."
            : "Couldn't load your matches. Please try again."
        );
      } finally {
        if (ticket === requestRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [tab, sort]
  );

  useEffect(() => {
    if (orgReady) load(0);
  }, [orgReady, load]);

  const loadProfile = useCallback(() => {
    fetchMatchProfile().then(setProfile).catch(() => {});
  }, []);
  useEffect(() => {
    if (orgReady) loadProfile();
  }, [orgReady, loadProfile]);

  // While a run is queued or going, poll quietly: a first visit fills itself in.
  const running = !!data && (data.status?.refreshing || data.status?.state === "matching");
  useEffect(() => {
    if (!running) return undefined;
    const t = setTimeout(() => load(0, { silent: true }), POLL_MS);
    return () => clearTimeout(t);
  }, [running, data, load]);

  // A run just finished: the profile's lines of work may have changed with it.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) loadProfile();
    wasRunning.current = running;
  }, [running, loadProfile]);

  // First visit: offer the setup once per session, never on every load.
  useEffect(() => {
    if (!data || data.setup_done || !data.status?.memory_ready) return;
    try {
      if (sessionStorage.getItem(SETUP_SEEN)) return;
      sessionStorage.setItem(SETUP_SEEN, "1");
    } catch {
      return;
    }
    setSetup({ step: 0 });
  }, [data]);

  // "New" resets when the person leaves, so the New tab stays useful while here.
  useEffect(() => () => {
    markDiscoverViewed().catch(() => {});
  }, []);

  // Being here is seeing the new matches: the bell entry they raised, and the
  // badge it puts on the sidebar and the Discover tab, clear for this workspace.
  const unreadMatches = useNotificationsStore((s) =>
    s.items
      .filter((n) => n.type === "tender_matches" && !n.is_read)
      .map((n) => n.id)
      .join(",")
  );
  useEffect(() => {
    if (!unreadMatches) return;
    const orgId = Cookies.get("selected_org_id");
    const { items, markRead } = useNotificationsStore.getState();
    items
      .filter(
        (n) =>
          n.type === "tender_matches" &&
          !n.is_read &&
          (!orgId || String(n.data?.organization_id) === String(orgId))
      )
      .forEach((n) => markRead(n.id));
  }, [unreadMatches]);

  // New matches arriving mid-session: a pill, never a reshuffle under the reader.
  const latest = useNotificationsStore((s) => s.items[0]);
  const seenRef = useRef(undefined);
  useEffect(() => {
    if (seenRef.current === undefined) {
      seenRef.current = latest?.id ?? null;
      return;
    }
    if (!latest || latest.id === seenRef.current) return;
    seenRef.current = latest.id;
    if (latest.type === "tender_matches") setPill(latest.data?.count || 1);
  }, [latest]);

  const stats = data?.stats || {};
  const status = data?.status;

  const replaceRow = (id, patch) =>
    setResults((prev) => prev.map((r) => (r.id === id ? { ...r, match: { ...r.match, ...patch } } : r)));

  const handleSave = async (row, saved) => {
    replaceRow(row.id, { saved });
    try {
      await saveMatch(row.match.id, saved);
      setData((d) => d && { ...d, stats: { ...d.stats, saved: (d.stats.saved || 0) + (saved ? 1 : -1) } });
      if (tab === "saved" && !saved) setResults((prev) => prev.filter((r) => r.id !== row.id));
    } catch {
      replaceRow(row.id, { saved: !saved });
      showErrorToast("Couldn't save that. Please try again.");
    }
  };

  const handleDismiss = async (row, reason) => {
    const before = results;
    setResults((prev) => prev.filter((r) => r.id !== row.id));
    if (panelId === row.id) setPanelId(null);
    try {
      await dismissMatch(row.match.id, { dismissed: true, reason });
      setData((d) =>
        d && { ...d, stats: { ...d.stats, not_a_fit: (d.stats.not_a_fit || 0) + 1, total: Math.max(0, (d.stats.total || 0) - 1) } }
      );
      showSuccessToast("Got it. Sensa won't show this one again.");
    } catch {
      setResults(before);
      showErrorToast("Couldn't update that. Please try again.");
    }
  };

  const handleRestore = async (row) => {
    setResults((prev) => prev.filter((r) => r.id !== row.id));
    try {
      await dismissMatch(row.match.id, { dismissed: false });
      load(0, { silent: true });
    } catch {
      showErrorToast("Couldn't restore that. Please try again.");
      load(0, { silent: true });
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshDiscover();
      showSuccessToast("Matching again. New fits appear here in a minute or two.");
      load(0, { silent: true });
    } catch (err) {
      showErrorToast(
        err?.response?.status === 429
          ? "Matches were refreshed within the hour. They also update after every tender sync."
          : "Couldn't start matching. Please try again."
      );
    } finally {
      setRefreshing(false);
    }
  };

  const handleTip = async (tip) => {
    if (!profile) return;
    const patch =
      tip.kind === "exclude_code"
        ? { exclude_codes: [...profile.exclude_codes.map((c) => c.code), tip.code] }
        : { include_codes: [...profile.include_codes.map((c) => c.code), tip.code] };
    try {
      setProfile(await updateMatchProfile(patch));
      showSuccessToast(tip.kind === "exclude_code" ? `Stopped matching ${tip.code}.` : `Added ${tip.code}.`);
      load(0, { silent: true });
    } catch {
      showErrorToast("Couldn't update your profile. Please try again.");
    }
  };

  const panelIndex = results.findIndex((r) => r.id === panelId);
  const panelRow = panelIndex >= 0 ? results[panelIndex] : null;

  const openProject = ({ projectId }) => {
    const orgId = Cookies.get("selected_org_id");
    navigate(
      orgId
        ? `/rfp-projects/project/${projectId}/org/${orgId}/project-files`
        : `/rfp-projects/project/${projectId}/project-files`
    );
  };

  const firstLoad = loading && !data;
  const headline =
    stats.new > 0
      ? `${stats.new} new tender${stats.new === 1 ? "" : "s"} fit your business`
      : stats.total > 0
        ? `${stats.total} tender${stats.total === 1 ? "" : "s"} fit your business`
        : "Tenders that fit your business";

  const empty = () => {
    if (status && !status.memory_ready) {
      return (
        <EmptyState
          title="Sensa needs your Memory to find tenders"
          body="Memory is built from the documents in your Knowledge Base. Upload a capability statement or past proposals, or add industry codes to your match profile to match by code alone."
          action="Set up my match profile"
          onAction={() => setSetup({ step: 1 })}
        />
      );
    }
    if (running && stats.total === 0) {
      return (
        <EmptyState
          title="Finding your first matches"
          body={`Sensa is checking ${(status?.open_tenders || 0).toLocaleString()} open tenders against your profile and reading the closest ones in detail. This takes a minute or two.`}
          progress
        />
      );
    }
    if (tab === "new" && stats.total > 0) {
      return (
        <EmptyState
          title="You're all caught up"
          body="Sensa keeps watching. You'll hear about the next good fit after the next tender update."
          action="Broaden my profile"
          onAction={() => setSetup({ step: 1 })}
        />
      );
    }
    if (tab === "saved") {
      return <EmptyState title="Nothing saved yet" body="Save a match to keep it here while you decide." />;
    }
    if (tab === "not_a_fit") {
      return <EmptyState title="Nothing marked as not a fit" body="Tenders you rule out land here, and can be restored." />;
    }
    return (
      <EmptyState
        title="No open tenders fit yet"
        body="Nothing open right now matches your profile. Sensa checks again after every tender update."
        action="Broaden my profile"
        onAction={() => setSetup({ step: 1 })}
      />
    );
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-[#FAFAFA] font-outfit">
      <div className="flex-shrink-0 bg-white">
        <Header name="Tender Search" icon="" isMenuOpen={isMenuOpen} onClick={() => {}} />
      </div>

      <div className="flex min-h-0 flex-1">
        <aside className={panelRow ? RAIL_CLASS_WITH_PANEL : RAIL_CLASS}>
          <DiscoverRail
            profile={profile}
            onTune={(step = 1) => setSetup({ step })}
            tips={data?.suggestions || []}
            onTip={handleTip}
          />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className={TOP_BAR_CLASS}>
            <div className="flex items-center gap-3">
              <TenderModeSwitch active="discover" newCount={stats.new || 0} />
              <StatusChip status={status} onRefresh={handleRefresh} refreshing={refreshing || running} />
              <SortSelect id="discover-sort" value={sort} onChange={setSort} options={SORTS} />
            </div>
          </div>

          <div className="custom-scrollbar relative flex-1 overflow-y-auto px-6 pt-4">
            <div className={`${CONTENT_CLASS} pb-10`}>
              {pill > 0 && (
                <div className="sticky top-2 z-10 flex justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      setPill(0);
                      load(0);
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-[#2B1A28] px-4 py-2 text-[13px] font-semibold text-white shadow-lg"
                  >
                    <ArrowUp className="h-3.5 w-3.5" strokeWidth={2.4} />
                    {pill} new match{pill === 1 ? "" : "es"} from the latest update
                    <span className="text-[#E7B6E0]">Show</span>
                  </button>
                </div>
              )}

              <h1 className="text-[24px] font-semibold tracking-[-0.01em] text-[#18101C]">{headline}</h1>
              <p className="mt-1 text-[14px] text-[#6B6472]">
                Ranked by how well each tender fits your profile. No searching needed.
              </p>

              <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatTile value={stats.strong} label="Strong fits" />
                <StatTile value={stats.good} label="Good fits" />
                <StatTile value={stats.closing_soon} label="Closing in 7 days" tone="text-[#B91C1C]" />
                <StatTile value={stats.saved} label="Saved" />
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3 border-b border-[#18101C]/[0.08]">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`-mb-px flex items-center gap-1.5 border-b-2 px-1 pb-2.5 pt-1 text-[14px] font-semibold transition-colors ${
                      tab === t.key ? "border-[#A04A97] text-[#8E4187]" : "border-transparent text-[#6B6472] hover:text-[#18101C]"
                    }`}
                  >
                    {t.label}
                    <span
                      className={`rounded-full px-1.5 text-[11.5px] ${
                        tab === t.key ? "bg-[#A04A97] text-white" : "bg-[#18101C]/[0.06] text-[#52525B]"
                      }`}
                    >
                      {stats[t.stat] ?? 0}
                    </span>
                  </button>
                ))}
              </div>

              <div className="pt-4">
                {error && <Callout tone="danger">{error}</Callout>}

                {firstLoad ? (
                  [0, 1, 2].map((i) => <CardSkeleton key={i} />)
                ) : results.length === 0 && !error ? (
                  empty()
                ) : (
                  <div aria-busy={loading} className={`transition-opacity ${loading ? "opacity-60" : ""}`}>
                    {results.map((row) => (
                      <DiscoverCard
                        key={row.id}
                        tender={row}
                        compact={!!panelRow}
                        active={row.id === panelId}
                        onOpen={(r) => setPanelId(r.id === panelId ? null : r.id)}
                        onSave={handleSave}
                        onDismiss={handleDismiss}
                        onRestore={handleRestore}
                      />
                    ))}
                  </div>
                )}

                {!loading && data?.has_more && (
                  <div className="pb-6 pt-2 text-center">
                    <button
                      type="button"
                      disabled={loadingMore}
                      onClick={() => load(results.length)}
                      className="rounded-lg border border-[#00000024] bg-white px-4 py-2 text-[14px] font-medium text-[#52525B] hover:bg-gray-50 disabled:opacity-60"
                    >
                      {loadingMore ? "Loading..." : "Load more matches"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {panelRow && (
          <CopilotTenderPanel
            key={`discover-${panelRow.id}`}
            tenderId={panelRow.id}
            initial={panelRow}
            visible
            onClose={() => setPanelId(null)}
            onProjectCreated={openProject}
            nav={{
              index: panelIndex,
              total: results.length,
              onPrev: () => panelIndex > 0 && setPanelId(results[panelIndex - 1].id),
              onNext: () => panelIndex < results.length - 1 && setPanelId(results[panelIndex + 1].id),
            }}
            onSave={tab === "not_a_fit" ? undefined : handleSave}
            onDismiss={tab === "not_a_fit" ? undefined : handleDismiss}
          />
        )}
      </div>

      {setup && (
        <MatchSetupModal
          open
          startStep={setup.step}
          notify={data?.viewer?.notify || "bell"}
          onClose={() => setSetup(null)}
          onSaved={(saved, notify) => {
            setSetup(null);
            setProfile(saved);
            setData((d) => d && { ...d, setup_done: true, viewer: { ...d.viewer, notify } });
            showSuccessToast("Profile saved. Sensa is matching against it now.");
            load(0, { silent: true });
          }}
        />
      )}
    </div>
  );
}
